// The pictures of patterns: a row of shapes or beads with gaps, and a
// growing pattern of towers or rows. Plain React SVG, like
// practice-count.tsx. A gap is an empty dashed box with "?" in it; the only
// words are "Step 1", "Step 2" and so on under a growing pattern.

import type { ReactNode } from 'react';
import {
  PATTERN_LABEL_SIZE,
  describePicturePattern,
  growingPicture,
  isColour,
  repeatingPicture,
  type GrowingPatternSpec,
  type PatternColour,
  type PatternItem,
  type PicturePatternSpec,
  type RepeatingPatternSpec,
} from './practice-picture-pattern-data';

const INK = '#2d4a3e';
const GAP_FILL = '#ffffff';

// Each shape has its own colour, but its outline is what tells it apart.
const SHAPE_FILLS = {
  circle: '#e0605a',
  square: '#6fa8dc',
  triangle: '#6aa86a',
  star: '#f2c94c',
  heart: '#e98bbf',
  diamond: '#7b5ea7',
  rectangle: '#f39c5a',
} as const;

const BEAD_FILLS: Record<PatternColour, string> = {
  red: '#d9534f',
  blue: '#4a7fc1',
  yellow: '#f2c94c',
  green: '#5a9e5a',
};

function points(list: [number, number][]): string {
  return list.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
}

// A bead of one colour, with a marking of its own: red plain, blue with a
// white band, yellow with dots and green with a white ring.
function Bead({
  colour,
  cx,
  cy,
  r,
}: {
  colour: PatternColour;
  cx: number;
  cy: number;
  r: number;
}) {
  const line = { stroke: INK, strokeWidth: 1.5 };
  return (
    <g data-item={colour}>
      <circle cx={cx} cy={cy} r={r} fill={BEAD_FILLS[colour]} {...line} />
      {colour === 'blue' && (
        <rect
          x={cx - r * 0.92}
          y={cy - r * 0.2}
          width={r * 1.84}
          height={r * 0.4}
          fill="#ffffff"
        />
      )}
      {colour === 'yellow' &&
        [-1, 0, 1].map((at) => (
          <circle
            key={at}
            cx={cx + at * r * 0.45}
            cy={cy + (at === 0 ? -r * 0.3 : r * 0.25)}
            r={r * 0.14}
            fill={INK}
          />
        ))}
      {colour === 'green' && (
        <circle
          cx={cx}
          cy={cy}
          r={r * 0.5}
          fill="none"
          stroke="#ffffff"
          strokeWidth={r * 0.18}
        />
      )}
    </g>
  );
}

function Gap({
  x,
  y,
  width,
  height,
}: {
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  return (
    <g data-gap="true">
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        rx="4"
        fill={GAP_FILL}
        stroke={INK}
        strokeWidth="1.5"
        strokeDasharray="4 3"
      />
      <text
        x={x + width / 2}
        y={y + height / 2}
        dy="0.35em"
        textAnchor="middle"
        fill={INK}
        fontSize={Math.min(18, height * 0.6)}
        fontWeight="700"
      >
        ?
      </text>
    </g>
  );
}

// One shape, bead or gap filling a square of side `size` whose top left
// corner is (x, y).
function PatternThing({
  item,
  x,
  y,
  size,
}: {
  item: PatternItem;
  x: number;
  y: number;
  size: number;
}) {
  const cx = x + size / 2;
  const cy = y + size / 2;
  const r = size / 2;
  const line = {
    stroke: INK,
    strokeWidth: 1.5,
    strokeLinejoin: 'round' as const,
  };
  if (item === '?') return <Gap x={x} y={y} width={size} height={size} />;
  if (isColour(item)) {
    return <Bead colour={item} cx={cx} cy={cy} r={r * 0.85} />;
  }
  const fill = SHAPE_FILLS[item];
  switch (item) {
    case 'circle':
      return (
        <g data-item="circle">
          <circle cx={cx} cy={cy} r={r * 0.88} fill={fill} {...line} />
        </g>
      );
    case 'square':
      return (
        <g data-item="square">
          <rect
            x={x + r * 0.12}
            y={y + r * 0.12}
            width={size - r * 0.24}
            height={size - r * 0.24}
            fill={fill}
            {...line}
          />
        </g>
      );
    case 'triangle':
      return (
        <g data-item="triangle">
          <polygon
            points={points([
              [cx, y + r * 0.1],
              [x + size - r * 0.08, y + size - r * 0.15],
              [x + r * 0.08, y + size - r * 0.15],
            ])}
            fill={fill}
            {...line}
          />
        </g>
      );
    case 'star':
      return (
        <g data-item="star">
          <polygon
            points={points(
              Array.from({ length: 10 }, (_, index) => {
                const turn = (index * Math.PI) / 5;
                const reach = index % 2 === 0 ? r * 0.95 : r * 0.42;
                return [
                  cx + Math.sin(turn) * reach,
                  cy - Math.cos(turn) * reach,
                ];
              }),
            )}
            fill={fill}
            {...line}
          />
        </g>
      );
    case 'heart':
      return (
        <g data-item="heart">
          <path
            d={`M ${String(cx)} ${String(y + size * 0.9)} C ${String(x - r * 0.1)} ${String(cy + r * 0.1)} ${String(x + r * 0.3)} ${String(y + r * 0.05)} ${String(cx)} ${String(y + r * 0.55)} C ${String(x + size - r * 0.3)} ${String(y + r * 0.05)} ${String(x + size + r * 0.1)} ${String(cy + r * 0.1)} ${String(cx)} ${String(y + size * 0.9)} Z`}
            fill={fill}
            {...line}
          />
        </g>
      );
    case 'diamond':
      return (
        <g data-item="diamond">
          <polygon
            points={points([
              [cx, y + r * 0.05],
              [x + size - r * 0.25, cy],
              [cx, y + size - r * 0.05],
              [x + r * 0.25, cy],
            ])}
            fill={fill}
            {...line}
          />
        </g>
      );
    case 'rectangle':
      return (
        <g data-item="rectangle">
          <rect
            x={x + r * 0.05}
            y={y + r * 0.45}
            width={size - r * 0.1}
            height={size - r * 0.9}
            fill={fill}
            {...line}
          />
        </g>
      );
  }
}

function Frame({
  spec,
  width,
  height,
  children,
}: {
  spec: PicturePatternSpec;
  width: number;
  height: number;
  children: ReactNode;
}) {
  // Towers are tall, so they are not grown past their natural size.
  const grow = spec.kind === 'growing' && spec.layout === 'tower' ? 1 : 1.3;
  return (
    <figure className="m-0 max-w-md">
      <svg
        role="img"
        aria-label={describePicturePattern(spec)}
        viewBox={`0 0 ${String(width)} ${String(height)}`}
        className="mx-auto block h-auto w-full"
        style={{ maxWidth: `${String(Math.round(width * grow))}px` }}
      >
        {children}
      </svg>
    </figure>
  );
}

function RepeatingView({ spec }: { spec: RepeatingPatternSpec }) {
  const picture = repeatingPicture(spec);
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      {picture.items.map((thing, index) => (
        <PatternThing
          key={index}
          item={thing.item}
          x={thing.x}
          y={thing.y}
          size={picture.size}
        />
      ))}
    </Frame>
  );
}

// One thing in a growing pattern. Blocks take turns between two shades so
// each one stands out from the next.
function GrowingThing({
  item,
  x,
  y,
  size,
  index,
}: {
  item: GrowingPatternSpec['item'];
  x: number;
  y: number;
  size: number;
  index: number;
}) {
  if (item === 'block') {
    return (
      <g data-item="block">
        <rect
          x={x}
          y={y}
          width={size}
          height={size}
          rx="2"
          fill={index % 2 === 0 ? '#6fa8dc' : '#a9cbe9'}
          stroke={INK}
          strokeWidth="1.5"
        />
      </g>
    );
  }
  if (item === 'counter') {
    return (
      <g data-item="counter">
        <circle
          cx={x + size / 2}
          cy={y + size / 2}
          r={size * 0.45}
          fill="#7b5ea7"
          stroke={INK}
          strokeWidth="1.5"
        />
      </g>
    );
  }
  return <PatternThing item="star" x={x} y={y} size={size} />;
}

function GrowingView({ spec }: { spec: GrowingPatternSpec }) {
  const picture = growingPicture(spec);
  const tower = spec.layout === 'tower';
  return (
    <Frame spec={spec} width={picture.width} height={picture.height}>
      {picture.steps.map((step) => (
        <g key={step.number} data-step={step.number}>
          {step.things.map((thing, index) => (
            <GrowingThing
              key={index}
              item={spec.item}
              x={thing.x}
              y={thing.y}
              size={picture.size}
              index={index}
            />
          ))}
          {step.gap !== null && <Gap {...step.gap} />}
          <text
            data-label="true"
            x={step.label.x}
            y={step.label.y}
            dy="0.35em"
            textAnchor={tower ? 'middle' : 'start'}
            fill={INK}
            fontSize={PATTERN_LABEL_SIZE}
          >
            {`Step ${String(step.number)}`}
          </text>
        </g>
      ))}
    </Frame>
  );
}

export function PicturePatternView({
  pattern,
}: {
  pattern: PicturePatternSpec;
}) {
  return pattern.kind === 'repeating' ? (
    <RepeatingView spec={pattern} />
  ) : (
    <GrowingView spec={pattern} />
  );
}
